import { type KeyboardEvent, memo, type RefObject } from 'react';

import { getWorkspaceAgentSelectAssetEmptyCopy } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/getWorkspaceAgentSelectAssetEmptyCopy';
import { getWorkspaceAgentSelectAssetActiveOptionId } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/getWorkspaceAgentSelectAssetOptionId';
import { useWorkspaceAgentSelectAssetSearchKeyDown } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/useWorkspaceAgentSelectAssetSearchKeyDown';
import {
  useWorkspaceAgentSelectAssetStore,
  type WorkspaceAgentSelectAssetStoreState,
} from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetStoreContext';
import { WorkspaceAgentSelectAssetVirtualList } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetVirtualList';

const getAssetHighlightedIndex = (state: WorkspaceAgentSelectAssetStoreState) => state.assetHighlightedIndex;

const getAssetResults = (state: WorkspaceAgentSelectAssetStoreState) => state.assetResults;

const handleAssetListEscape = (event: KeyboardEvent<HTMLElement>, state: WorkspaceAgentSelectAssetStoreState) => {
  event.preventDefault();
  state.setOpen(false);
};

export type WorkspaceAgentSelectAssetListProps = {
  listRef: RefObject<HTMLDivElement>;
};

export const WorkspaceAgentSelectAssetList = memo(({ listRef }: WorkspaceAgentSelectAssetListProps) => {
  const results = useWorkspaceAgentSelectAssetStore((state) => state.assetResults);
  const query = useWorkspaceAgentSelectAssetStore((state) => state.query);
  const selectedBoard = useWorkspaceAgentSelectAssetStore((state) => state.selectedBoard);
  const source = useWorkspaceAgentSelectAssetStore((state) => state.source);
  const highlightedIndex = useWorkspaceAgentSelectAssetStore((state) => state.assetHighlightedIndex);
  const isKeyboardHighlight = useWorkspaceAgentSelectAssetStore((state) => state.isAssetKeyboardHighlight);
  const moveHighlight = useWorkspaceAgentSelectAssetStore((state) => state.moveAssetHighlight);
  const onHighlight = useWorkspaceAgentSelectAssetStore((state) => state.highlightAsset);
  const onSelectItem = useWorkspaceAgentSelectAssetStore((state) => state.selectItemById);
  const activeOptionId = getWorkspaceAgentSelectAssetActiveOptionId(results.items, highlightedIndex);
  const { handleKeyDown } = useWorkspaceAgentSelectAssetSearchKeyDown({
    getHighlightedIndex: getAssetHighlightedIndex,
    getResults: getAssetResults,
    moveHighlight,
    onEscape: handleAssetListEscape,
  });
  const hasRows = results.items.length > 0;
  const emptyStateTitle = getWorkspaceAgentSelectAssetEmptyCopy({
    hasFailed: results.isError && !hasRows,
    isBoardPickerOpen: false,
    query,
    source,
  });

  const listPaginationKey = `${source}|${selectedBoard?.id ?? ''}|${query}`;

  return (
    <WorkspaceAgentSelectAssetVirtualList
      activeOptionId={activeOptionId}
      ariaLabel="Select asset"
      emptyStateTitle={emptyStateTitle}
      highlightedIndex={highlightedIndex}
      isKeyboardHighlight={isKeyboardHighlight}
      listPaginationKey={listPaginationKey}
      listRef={listRef}
      onHighlight={onHighlight}
      onKeyDown={handleKeyDown}
      onSelectItem={onSelectItem}
      results={results}
      role="listbox"
    />
  );
});

WorkspaceAgentSelectAssetList.displayName = 'WorkspaceAgentSelectAssetList';
